import { z } from 'zod';
import type { AiJobType } from '~/services/websocket/message-types';
import { optionSchema, requiredOptionSchema } from '../../types';

// Types for AI parsing jobs
export interface AIParseJob {
  id: string | number;
  document_id: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  job_status: 'PENDING' | 'STARTED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress: number;
  error?: string;
  result?: unknown;
  process_confidence?: number | null;
  created_at: string;
  updated_at: string;
}

// Old AI job update format (for backwards compatibility)
export interface WSAIParseJobData {
  jobId: string | number;
  documentId: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  error?: string;
  result?: unknown;
}

// New AI job update format
export interface WSAiJobUpdateData {
  jobId: string | number;
  documentId: number;
  jobType: AiJobType;
  status: 'PENDING' | 'STARTED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress?: number;
  stage?: string;
  error?: string;
  resultId?: number;
}

export interface DocumentReference {
  id: number;
  providerId: number;
  docCategory: string;
  docDesc: string;
  docName: string;
  docType: string;
  // The stored file's name (MCV-1802): what the edit form checks the File Type against.
  uploadedFileName?: string | null;
  createdAt: string;
  updatedAt: string;
  uploadedAt: string;
  ownerId: string;
  uploaderId: string;
  status: string;
}

export interface DocumentWithAiJobs {
  id: number;
  providerId: number;
  docCategory: string;
  docDesc: string;
  docName: string;
  docType: string;
  // The stored file's name (MCV-1802): what the edit form checks the File Type against.
  uploadedFileName?: string | null;
  createdAt: string;
  updatedAt: string;
  uploadedAt: string;
  ownerId: string;
  uploaderId: string;
  status: string;
  aiJobs: AIParseJob[];
}

export interface DocumentWithAIStatus extends DocumentReference {
  aiJob: AIParseJob | null;
}

/** The API's POST /documents response: the document a successful upload created. */
export interface UploadedDocument {
  id: number;
  docName: string;
  // [MCV-1802] The file's own name, for the edit form's format check. The upload response does
  // not return it, so the batch sets it from the file it sent.
  uploadedFileName?: string | null;
  docType?: string;
  docCategory?: string;
  docDesc?: string;
}

export interface DocumentParseResp {
  message: string;
}

export interface Context {
  fileDrag: boolean;
  modalOpen: boolean;
  form: {
    docCategory: { label: string; value: string };
    docType: { label: string; value: string };
    docDesc: string;
    docName: string;
    // Set when editing an existing document (MCV-1802).
    uploadedFileName?: string | null;
    id: number;
  };
  documentReferences: DocumentReference[];
  providerId?: number;
  loading: boolean;
}

export const DocumentInfoSchema = z.object({
  docCategory: optionSchema.optional(),
  docName: z
    .string()
    .trim()
    .min(1, { message: 'Name is required' })
    .max(64, { message: 'Name must be 64 characters or less' }),
  // [MCV-1765] No silent fallback to 'other': the user picks a File Type.
  docType: requiredOptionSchema('File Type is required'),
  docDesc: z.string().max(256, { message: 'Description must be 256 characters or less' }),
  // MCV-1765: Add Document with 2+ packet files: merge them into ONE packet document.
  combineFiles: z.boolean().optional(),
});
export type DocumentInfoValues = z.infer<typeof DocumentInfoSchema>;

/**
 * [MCV-1765] A file the Add Document modal uploads, with the File Type chosen for it.
 * [MCV-1848] And its own File Category, when one was chosen (empty or absent: none).
 */
export interface StagedUpload {
  file: File;
  docType: string;
  docCategory?: string;
}

/**
 * [MCV-1765] Each staged file carries its own File Type (and is checked on its own). [MCV-1810]
 * The Add Document form always uses this schema: it has no shared File Type field, so the form's
 * own docType may stay empty. Editing an existing document uses DocumentInfoSchema.
 */
export const MultiFileInfoSchema = DocumentInfoSchema.extend({ docType: optionSchema });
